import { CompositeFilterDescriptor, isCompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Chip, ChipList, ChipListDataChangeEvent, ChipProps } from '@progress/kendo-react-buttons';
import { differenceBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { IFilters } from 'src/types/PostBody';
import { filterOperatorsValue } from 'src/types/filter';
import { modifyFilters } from 'src/utils/requestInterceptor';

const chipListData = [
  { field: 'code', title: 'GL Code', dataType: 'String' },
  { field: 'description', title: 'Description', dataType: 'String' },
  { field: 'budgetAmount', title: 'Forecast Amt', dataType: 'Number' },
  { field: 'holdAmount', title: 'Planned Amt', dataType: 'Number' },
  { field: 'committedAmount', title: 'Committed Amt', dataType: 'Number' },
  { field: 'spentAmount', title: 'Actualized Amt', dataType: 'Number' },
  { field: 'remainingBudgetAmount', title: '$ Remaining Forecast Amt', dataType: 'Number' },
  {
    field: 'remainingBudgetAmountPercentage',
    title: '% Remaining Forecast Amt',
    dataType: 'Number',
  },
  { field: 'isFixed', title: 'Fixed Code', dataType: 'Boolean' },
  { field: 'isActive', title: 'Active Code', dataType: 'Boolean' },
  { field: 'initialBudget', title: 'Budget Amt', dataType: 'Number' },
  { field: 'remainingInitialBudgetAmount', title: '$ Remaining Budget Amt', dataType: 'Number' },
  {
    field: 'remainingInitialBudgetAmountPercentage',
    title: '% Remaining Budget Amt',
    dataType: 'Number',
  },
  { field: 'invoiceApproval', title: 'Invoice Approval', dataType: 'String' },
  { field: 'poApproval', title: 'PO Approval', dataType: 'String' },
];
interface ChipListData {
  field: string;
  title: string;
}

const PeriodFilterPills = () => {
  const { periodCheckbookDataState, setPeriodCheckbookDataState } = useViewCheckbook();
  const filterData: IFilters[] = modifyFilters(periodCheckbookDataState.filter);
  const pillsData = useMemo(() => {
    let data: ChipListData[] = [];
    filterData.forEach((f) => {
      chipListData.forEach((c) => {
        if (f.field === c.field) {
          const filterOperator = filterOperatorsValue.find(
            (operator) => operator.operator === f.operator
          );
          const fieldValue =
            c.dataType === 'String'
              ? `"${f.value ?? ''}"`
              : c.dataType === 'Boolean'
              ? f.value === 'true'
                ? 'Yes'
                : 'No'
              : f.value ?? '';
          const title = `${c.title} ${filterOperator?.value} ${fieldValue}`;
          data.push({ ...c, title });
        }
        return;
      });
    });
    return data;
  }, [filterData]);

  const filterGroupByField = (field: string, filter: CompositeFilterDescriptor | undefined) => {
    const compositeFilters = filter?.filters.filter((f) => {
      if (isCompositeFilterDescriptor(f)) {
        return (
          f.filters &&
          f.filters.length &&
          f.filters.length <= 2 &&
          f.filters.find((nf) => isCompositeFilterDescriptor(nf) || nf.field !== field)
        );
      }
      return false;
    });
    return compositeFilters ?? [];
  };

  const handleDataChange = useCallback(
    (event: ChipListDataChangeEvent) => {
      const value = differenceBy(pillsData, event.value, 'field')[0];
      const filters = filterGroupByField(value.field, periodCheckbookDataState.filter);
      setPeriodCheckbookDataState((prev) => ({ ...prev, filter: { logic: 'and', filters } }));
    },
    [periodCheckbookDataState.filter, pillsData, setPeriodCheckbookDataState]
  );
  return (
    <div className="mb-2">
      <ChipList
        data={pillsData}
        selection="single"
        textField="title"
        valueField="field"
        onDataChange={handleDataChange}
        size={'small'}
        chip={(props: ChipProps) => <Chip removable={true} {...props} />}
      />
    </div>
  );
};

export default PeriodFilterPills;
